import {
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { listStoresForOrg, type Store } from '@pc/db';
import { Button, Modal, SmallCap } from '@pc/ui';
import { useActiveContext } from '../auth/ActiveContextProvider';

/**
 * localStorage key remembering which store the operator last picked from
 * an org-mode action affordance. Default-selects that store next time so
 * a multi-store operator who mostly works out of one location doesn't
 * have to scroll the list every time. Distinct from `pc.activeStoreId`
 * (which is "last entered store" — committed context, not just a hint).
 */
const LS_LAST_ORG_MODE_STORE = 'pc.lastOrgModeStoreId';

/**
 * Bridges the gap between the org-mode read-only pivots (Dashboard,
 * Clients, Orders) and the per-store create flows. Org-mode entities
 * (clients, orders, appointments) all have NOT NULL `store_id`, so any
 * write has to pick a store first. Rather than duplicate every create
 * form with an org-mode "Choose Store" variant, we drop the operator
 * into the existing per-store form with the store already selected.
 *
 * Behavior:
 *   * 1 store available -> no modal; immediately `setActiveStore(only)`
 *     + `navigate(path)`. Mode flips to 'store' inside the provider,
 *     `App.tsx` rebuilds its router keyed on `mode`, and the new tree
 *     mounts the per-store route at `path`.
 *   * N stores available -> open the modal with the org's stores. The
 *     default selection is `pc.lastOrgModeStoreId` from localStorage
 *     (falls back to the first store). On confirm, same handoff.
 *
 * Returns:
 *   * `trigger(path)` -- call from a button onClick to start the flow.
 *   * `modal` -- render inside your page's JSX tree.
 *
 * Why not `window.location.assign(path)` like `OrgStorePicker`? That
 * picker hard-navigates because it's already on `/choose-context` and
 * needs to swap to a different shell entirely. Our case is symmetric
 * to `OrgClientsPage.openClient` -- the user is mid-app, the URL is
 * about to change, and a soft `navigate()` after `setActiveStore` works
 * because react-router's history singleton is shared across router
 * instances during the swap.
 */
export function useChooseStoreThen(): {
  trigger: (path: string) => void;
  modal: ReactNode;
} {
  const { activeOrgId, setActiveStore } = useActiveContext();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [targetPath, setTargetPath] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Stores per org are tiny (1-10 typical, capped at 500 by the service
  // layer). Cached for 5 min and shared with other consumers of the same
  // key so opening the modal repeatedly is free.
  const storesQ = useQuery({
    queryKey: ['org-mode', 'choose-store', activeOrgId],
    queryFn: () => listStoresForOrg(activeOrgId!),
    enabled: !!activeOrgId,
    staleTime: 5 * 60_000,
  });

  const stores = useMemo<Store[]>(() => storesQ.data ?? [], [storesQ.data]);

  const enterStore = useCallback(
    (storeId: string, path: string) => {
      try {
        localStorage.setItem(LS_LAST_ORG_MODE_STORE, storeId);
      } catch {
        // iOS in-app browsers can throw on localStorage writes; ignore.
      }
      setActiveStore(storeId);
      navigate(path);
    },
    [setActiveStore, navigate],
  );

  const trigger = useCallback(
    (path: string) => {
      if (stores.length === 1) {
        enterStore(stores[0]!.id, path);
        return;
      }
      let defaultId: string | null = null;
      try {
        defaultId = localStorage.getItem(LS_LAST_ORG_MODE_STORE);
      } catch {
        defaultId = null;
      }
      if (!defaultId || !stores.some((s) => s.id === defaultId)) {
        defaultId = stores[0]?.id ?? null;
      }
      setSelectedId(defaultId);
      setTargetPath(path);
      setOpen(true);
    },
    [stores, enterStore],
  );

  const close = useCallback(() => {
    setOpen(false);
    setTargetPath(null);
  }, []);

  const confirm = useCallback(() => {
    if (!selectedId || !targetPath) return;
    enterStore(selectedId, targetPath);
    close();
  }, [selectedId, targetPath, enterStore, close]);

  const modal = (
    <Modal open={open} onClose={close} title={t('org.chooseStore.title')}>
      <p className="text-sm text-ink-2 mb-4">
        {t('org.chooseStore.subtitle')}
      </p>
      {storesQ.isLoading ? (
        <SmallCap>{t('actions.loading')}</SmallCap>
      ) : storesQ.isError ? (
        <p className="text-sm text-clay mb-4">
          {t('org.chooseStore.loadError')}
        </p>
      ) : stores.length === 0 ? (
        <p className="text-sm text-clay mb-4">
          {t('org.chooseStore.noStores')}
        </p>
      ) : (
        <ul
          role="radiogroup"
          aria-label={t('org.chooseStore.title')}
          className="flex flex-col border border-rule divide-y divide-rule mb-4"
        >
          {stores.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                role="radio"
                aria-checked={selectedId === s.id}
                onClick={() => setSelectedId(s.id)}
                className={
                  'w-full text-left px-3 py-2 flex items-baseline justify-between gap-3 ' +
                  (selectedId === s.id
                    ? 'bg-paper-2/60 text-ink'
                    : 'bg-paper text-ink-2 hover:bg-linen')
                }
              >
                <span className="text-sm">{s.name}</span>
                <span className="text-xs text-muted tabular">/{s.slug}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={close}>
          {t('actions.cancel')}
        </Button>
        <Button
          onClick={confirm}
          disabled={!selectedId || storesQ.isLoading || stores.length === 0}
        >
          {t('org.chooseStore.confirm')}
        </Button>
      </div>
    </Modal>
  );

  return { trigger, modal };
}
